import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEBOUNCE_MS = 220;

/**
 * Generic search-as-you-type field. Renders a list of suggestion rows under
 * an input. `endpoint` is the relative admin endpoint to hit, `queryKey` is
 * the search param name (default "q"), `renderRow` returns the JSX for each
 * suggestion, `onSelect` receives the picked row.
 *
 * The component is intentionally uncontrolled (the parent does not own the
 * draft text) so it can be reused as either a single-pick field (clears on
 * select) or a multi-pick adder (caller decides what to do with the row).
 */
export const AdminAutocomplete = ({
  endpoint,
  headers,
  queryKey = 'q',
  extraParams = null,
  placeholder = 'Start typing…',
  renderRow,
  onSelect,
  emptyHint = 'No matches',
  minChars = 1,
  dataTestId = 'admin-autocomplete',
  initialFocus = false,
  inputStyle,
  clearOnSelect = true,
}) => {
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (initialFocus && inputRef.current) inputRef.current.focus();
  }, [initialFocus]);

  // Outside-click close.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback(async (needle) => {
    setLoading(true);
    try {
      const params = { [queryKey]: needle, ...(extraParams || {}) };
      const res = await axios.get(`${API}${endpoint}`, { headers, params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, headers, queryKey, extraParams]);

  const handleChange = (value) => {
    setText(value);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < minChars) {
      // Still fetch (empty needle) to show top suggestions when minChars=0.
      if (minChars === 0) {
        debounceRef.current = setTimeout(() => fetchSuggestions(''), DEBOUNCE_MS);
      } else {
        setRows([]);
      }
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(value), DEBOUNCE_MS);
  };

  const handleSelect = (row) => {
    onSelect?.(row);
    if (clearOnSelect) setText('');
    setRows([]);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }} data-testid={dataTestId}>
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (minChars === 0 || text.length >= minChars) { setOpen(true); fetchSuggestions(text); } }}
          placeholder={placeholder}
          data-testid={`${dataTestId}-input`}
          style={{
            width: '100%',
            padding: '10px 36px 10px 32px',
            border: '1px solid #E7E7E7',
            borderRadius: 12,
            fontSize: 14,
            background: '#FFF',
            ...(inputStyle || {}),
          }}
        />
        {text ? (
          <button
            type="button"
            onClick={() => { setText(''); setRows([]); setOpen(false); }}
            data-testid={`${dataTestId}-clear`}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#888',
            }}
          >
            <X size={14} />
          </button>
        ) : loading ? (
          <Loader2 size={14} className="animate-spin" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
        ) : null}
      </div>
      {open && (text.length >= minChars || minChars === 0) ? (
        <div
          data-testid={`${dataTestId}-dropdown`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: '#FFF',
            border: '1px solid #E7E7E7',
            borderRadius: 12,
            boxShadow: '0 14px 30px rgba(15,17,21,0.14)',
            zIndex: 50,
            maxHeight: 320,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {loading && rows.length === 0 ? (
            <p style={{ padding: 12, margin: 0, fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={12} className="animate-spin" /> Searching…
            </p>
          ) : null}
          {!loading && rows.length === 0 ? (
            <p style={{ padding: 12, margin: 0, fontSize: 12, color: '#888' }}>{emptyHint}</p>
          ) : null}
          {rows.map((row, idx) => (
            <button
              key={row.id || idx}
              type="button"
              onClick={() => handleSelect(row)}
              data-testid={`${dataTestId}-row-${row.id || idx}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                borderRadius: 8,
                gap: 10,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F4F5F8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {renderRow ? renderRow(row) : (
                <span style={{ fontSize: 13, color: '#111' }}>{row.label || row.id}</span>
              )}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AdminAutocomplete;
